import { Injectable } from '@angular/core';
import { EPlatform, EPlatformFeature } from '@enums/campaign.enum';
import { ICreator } from '@pages/creators/creators.interface';
import { ALL_CREATORS_ID } from '@shared/constants/all-creators.constants';
import { camelCase, intersection } from 'lodash';
import { ICtaOption } from './cta-select.interface';
import { IDeliverableConfigurationApi } from './deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';

@Injectable({
  providedIn: 'root',
})
export class CtaSelectService {
  public static readonly allCtaOptions: Record<EPlatformFeature, ICtaOption> = {
    [EPlatformFeature.twitchStream]: {
      icon: 'icons/twitch.svg',
      id: 1,
      name: null,
      platform: EPlatform.twitch,
      platformFeature: EPlatformFeature.twitchStream,
    },
    [EPlatformFeature.instagramPost]: {
      icon: 'icons/instagram.svg',
      id: 2,
      name: null,
      platform: EPlatform.instagram,
      platformFeature: EPlatformFeature.instagramPost,
    },
    [EPlatformFeature.instagramReel]: {
      icon: 'icons/instagram-reel.svg',
      id: 3,
      name: null,
      platform: EPlatform.instagram,
      platformFeature: EPlatformFeature.instagramReel,
    },
    [EPlatformFeature.instagramStory]: {
      icon: 'icons/instagram-story.svg',
      id: 4,
      name: null,
      platform: EPlatform.instagram,
      platformFeature: EPlatformFeature.instagramStory,
    },
    [EPlatformFeature.tiktokPost]: {
      icon: 'icons/tiktok.svg',
      id: 5,
      name: null,
      platform: EPlatform.tiktok,
      platformFeature: EPlatformFeature.tiktokPost,
    },
    [EPlatformFeature.facebookPost]: {
      icon: 'icons/facebook.svg',
      id: 6,
      name: null,
      platform: EPlatform.facebook,
      platformFeature: EPlatformFeature.facebookPost,
    },
    [EPlatformFeature.xPost]: {
      icon: 'icons/x-post.svg',
      id: 7,
      name: null,
      platform: EPlatform.x,
      platformFeature: EPlatformFeature.xPost,
    },
    [EPlatformFeature.youtubeShort]: {
      icon: 'icons/youtube-shorts.svg',
      id: 8,
      name: null,
      platform: EPlatform.youtube,
      platformFeature: EPlatformFeature.youtubeShort,
    },
    [EPlatformFeature.youtubeVod]: {
      icon: 'icons/youtube.svg',
      id: 9,
      name: null,
      platform: EPlatform.youtube,
      platformFeature: EPlatformFeature.youtubeVod,
    },
    [EPlatformFeature.youtubeStream]: {
      icon: 'icons/youtube-live.svg',
      id: 10,
      name: null,
      platform: EPlatform.youtube,
      platformFeature: EPlatformFeature.youtubeStream,
    },
  } as const as Record<EPlatformFeature, ICtaOption>;

  public getAvailableCtaOptions(
    deliverableConfigurations: IDeliverableConfigurationApi[] | null,
    creators: ICreator[],
    validPlatforms: EPlatform[] | null,
  ): ICtaOption[] {
    const commonPlatforms = this.findCommonPlatforms(creators);
    const allCreatorsExists = creators.some((creator) => creator.id === ALL_CREATORS_ID);

    if (!deliverableConfigurations || !validPlatforms) {
      return [];
    }

    const validPlatformSet = new Set(validPlatforms.map((platform) => camelCase(platform) as string));

    return Object.values(CtaSelectService.allCtaOptions).filter(
      (cta) =>
        deliverableConfigurations.some(
          (config) => camelCase(config.platformFeature) === camelCase(cta.platformFeature),
        ) &&
        validPlatformSet.has(camelCase(cta.platform) as string) &&
        (allCreatorsExists || commonPlatforms?.some((platform) => camelCase(platform) === camelCase(cta.platform))),
    );
  }

  public findCommonPlatforms(creators: ICreator[]): EPlatform[] {
    return intersection(...creators.map((creator) => creator.channels?.map((channel) => channel.platform) ?? []));
  }
}
